import { getSupabase } from 'src/utils/supabase';

import { getCurrentUserId } from './secureServiceWrapper';

export const BLOCKED_STATUSES = [
  'locked',
  'expired',
  'canceled',
  'pending_payment',
  'trial_expired',
  'paused',
] as const;
export type BlockedStatus = (typeof BLOCKED_STATUSES)[number];

export const ALL_STATUSES = [
  'trialing',
  'active',
  'past_due',
  'canceled',
  'expired',
  'locked',
  'pending_payment',
  'trial_expired',
  'paused',
] as const;

export const UNLIMITED_PLANS = ['professional', 'enterprise'] as const;

export const STARTER_DEFAULT_FEATURES: Record<string, number> = {
  max_clients: 20,
  max_research: 20,
  max_contract_reviews: 20,
  max_document_uploads: 40,
  max_enhancements: 15,
};

export function isBlockedStatus(status: string): boolean {
  return (BLOCKED_STATUSES as readonly string[]).includes(status);
}

export function isUnlimitedPlan(planName: string): boolean {
  return (UNLIMITED_PLANS as readonly string[]).includes(planName);
}

export function isTrialExpired(status: string, trialEnd: string | null): boolean {
  if (status !== 'trialing' || !trialEnd) return false;
  return new Date(trialEnd) < new Date();
}

export function computeIsLocked(status: string, trialEnd: string | null): boolean {
  if (isBlockedStatus(status)) return true;
  if (isTrialExpired(status, trialEnd)) return true;
  return false;
}

export function computeTrialDaysRemaining(status: string, trialEnd: string | null): number {
  if (status !== 'trialing' || !trialEnd) return 0;
  const diff = Math.ceil((new Date(trialEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
}

interface FeatureCheckResult {
  allowed: boolean;
  current: number;
  limit: number;
}

export function checkFeatureUsage(
  planName: string | undefined,
  features: Record<string, number> | undefined,
  featureKey: string,
  usage: { feature_key: string; count: number }[],
  isLocked: boolean
): FeatureCheckResult {
  if (isLocked) {
    return { allowed: false, current: 0, limit: 0 };
  }

  if (planName && isUnlimitedPlan(planName)) {
    return { allowed: true, current: 0, limit: -1 };
  }

  const effectiveFeatures = features ?? STARTER_DEFAULT_FEATURES;
  const limit = effectiveFeatures[`max_${featureKey}`] ?? effectiveFeatures[featureKey] ?? 0;

  if (limit === -1) {
    return { allowed: true, current: 0, limit: -1 };
  }

  const current =
    featureKey === 'clients'
      ? usage.filter((u) => u.feature_key === 'clients').reduce((sum, u) => sum + u.count, 0)
      : usage.find((u) => u.feature_key === featureKey)?.count || 0;

  return { allowed: current < limit, current, limit };
}

export async function assertCanUseFeature(_featureContext?: string): Promise<void> {
  const userId = await getCurrentUserId();
  if (!userId) return;

  const supabase = await getSupabase();
  const { data: sub } = await supabase
    .from('user_subscriptions')
    .select('status, trial_end, subscription_plans!plan_id(name)')
    .eq('user_id', userId)
    .single();

  if (!sub) return;

  const plans = sub.subscription_plans as unknown;
  const planName = Array.isArray(plans) ? plans[0]?.name : (plans as { name: string } | null)?.name;

  if (planName && isUnlimitedPlan(planName) && !isBlockedStatus(sub.status)) {
    return;
  }

  if (isBlockedStatus(sub.status)) {
    throw new Error(
      sub.status === 'pending_payment'
        ? 'Your subscription requires activation before using this feature.'
        : `Your subscription is ${sub.status}. Please upgrade to continue.`
    );
  }

  if (isTrialExpired(sub.status, sub.trial_end)) {
    throw new Error('Your trial has ended. Please upgrade to continue.');
  }
}

export async function incrementUsage(featureKey: string): Promise<void> {
  const userId = await getCurrentUserId();
  if (!userId) return;

  const supabase = await getSupabase();
  await supabase.rpc('increment_usage', {
    p_user_id: userId,
    p_feature_key: featureKey,
  });
}

export async function checkAndIncrementUsageClient(
  featureKey: string
): Promise<{ allowed: boolean; current: number; limit: number }> {
  const userId = await getCurrentUserId();
  if (!userId) return { allowed: false, current: 0, limit: 0 };

  const supabase = await getSupabase();
  const { data: sub } = await supabase
    .from('user_subscriptions')
    .select('status, trial_end, subscription_plans!plan_id(name)')
    .eq('user_id', userId)
    .single();

  if (sub) {
    if (isBlockedStatus(sub.status)) return { allowed: false, current: 0, limit: 0 };
    if (isTrialExpired(sub.status, sub.trial_end)) return { allowed: false, current: 0, limit: 0 };

    const plans = sub.subscription_plans as unknown;
    const planName = Array.isArray(plans) ? plans[0]?.name : (plans as { name: string } | null)?.name;

    if (planName && isUnlimitedPlan(planName)) return { allowed: true, current: 0, limit: -1 };
  }

  const { data, error } = await supabase.rpc('increment_usage', {
    p_user_id: userId,
    p_feature_key: featureKey,
  });

  if (error || !data || data.length === 0) return { allowed: false, current: 0, limit: 0 };

  const result = data[0];
  if (!result.allowed) {
    await supabase.rpc('decrement_usage', { p_user_id: userId, p_feature_key: featureKey });
  }
  return { allowed: result.allowed, current: result.current_count, limit: result.max_limit };
}

export async function decrementUsageClient(featureKey: string): Promise<void> {
  const userId = await getCurrentUserId();
  if (!userId) return;

  const supabase = await getSupabase();
  await supabase.rpc('decrement_usage', {
    p_user_id: userId,
    p_feature_key: featureKey,
  });
}
